// src/components/partial-pages/AlertsView/Partials/VenueMap3.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react'
import '@mappedin/mappedin-js/lib/index.css'
import { TCameraTarget, getMapData, show3dMap, Coordinate } from '@mappedin/mappedin-js'
import { BRAND_COLOR } from 'lib/constants'
import { AlertSession } from 'lib/types'
import { useGlobal } from 'contexts/GlobalProvider'

import {
  postFingerprints,
  deleteFingerprint,
  getAllFingerprintsForFloor,
  deleteFingerprintsByCoordinates,
} from 'models/api/fingerprints'
import { getFloors } from 'models/api/floors'

type Props = {
  alertSession: AlertSession
  fingerprintMode: boolean
  showAllFingerprints: boolean
  selectedFloor: any
  lastBeacons: any
  lastFingerprints: any
  setCanUseMappedin: Function
}

function beaconsToMap(beacons: { beaconId: string; rssi: number }[]): Record<string, number> {
  return beacons.reduce((acc, { beaconId, rssi }) => {
    acc[beaconId] = rssi
    return acc
  }, {} as Record<string, number>)
}

function errorToColor(error: number): string {
  const clamped = Math.min(Math.max(error, 0), 20)
  const t = clamped / 12
  const r = t < 0.5 ? Math.round(510 * t) : 255
  const g = t < 0.5 ? 255 : Math.round(510 * (1 - t))
  const b = 0
  return `rgb(${r},${g},${b})`
}

/** Choose the fingerprint with the *lowest error* (best). Missing error -> +Infinity so it won't win. */
function pickFingerprintWithLowestError(items: any[]) {
  const score = (fp: any) =>
    typeof fp?.error === 'number' && Number.isFinite(fp.error) ? fp.error : Number.POSITIVE_INFINITY

  let best = items[0]
  let bestScore = score(best)
  for (let i = 1; i < items.length; i++) {
    const s = score(items[i])
    if (s < bestScore) {
      best = items[i]
      bestScore = s
    }
  }
  return best
}

/** Group by rounded (lat,lon) on a specific floor, return exactly one (lowest-error) fingerprint per spot */
function dedupeFingerprintsBySpot(fps: any[], floorId: string | number, precision = 5) {
  const groups = new Map<string, any[]>()
  fps
    .filter(fp => fp.floorId === floorId && typeof fp.lat === 'number' && typeof fp.lon === 'number')
    .forEach(fp => {
      const key = `${fp.lat.toFixed(precision)},${fp.lon.toFixed(precision)}`
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(fp)
    })

  const representatives = Array.from(groups.values()).map(items => pickFingerprintWithLowestError(items))
  return representatives
}

// ---- Helper to satisfy mappedin Coordinate typing (floorId?: string) ----
const toFloorId = (id: string | number | undefined | null) => (id == null ? undefined : String(id))

const VenueMap3 = ({
  alertSession,
  fingerprintMode,
  showAllFingerprints,
  selectedFloor,
  lastBeacons,
  lastFingerprints,
  setCanUseMappedin,
}: Props) => {
  const [initialized, setInitialized] = useState(false)
  const [mapData, setMapData] = useState<any>()
  const [mapView, setMapView] = useState<any>(null)
  const [lastLocation, setLastLocation] = useState<any>(null)
  const [cameraPosition, setCameraPosition] = useState<TCameraTarget | null>(null)
  const [fingerprintMarkers, setFingerprintMarkers] = useState<any[]>([])
  // All stored fingerprints for the selected floor, fetched when "show all" is on.
  const [allFingerprints, setAllFingerprints] = useState<any[]>([])
  // Bumped after a cluster delete to force a re-fetch of the floor's fingerprints.
  const [refreshTick, setRefreshTick] = useState(0)

  // The fingerprints to plot: every stored one for the floor when "show all" is
  // on, otherwise just this alert session's own fingerprints.
  const fingerprintData = showAllFingerprints ? allFingerprints : lastFingerprints

  // Track what we last centered on to avoid jitter
  const lastCameraKeyRef = useRef<string | null>(null)

  // Hold the live map view so the unmount teardown can destroy the WebGL context.
  const mapViewRef = useRef<any>(null)

  const { selectedOrg } = useGlobal()
  const lastBeaconsRef = useRef(lastBeacons)

  const dot = (color: string, size: string) => `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;">
      <div style="width:${size};height:${size};background-color:${color};border-radius:50%;box-shadow:0 0 6px #333c4e;"></div>
    </div>
  `

  // Expose for inline delete buttons rendered inside the marker HTML. Reassigned
  // on every render so the cluster handler always closes over the current org +
  // floor and can trigger a refresh once the delete resolves.
  ;(window as any).deleteFingerprint = deleteFingerprint
  ;(window as any).deleteFingerprintCluster = async (lat: number, lon: number) => {
    if (!alertSession?.orgId || !selectedFloor?.id) return
    await deleteFingerprintsByCoordinates({
      orgId: alertSession.orgId,
      floorId: selectedFloor.id,
      coordinates: [{ lat, lon }],
    })
    setRefreshTick(tick => tick + 1)
  }

  // Single-fingerprint marker UI
  const fpDot = (color: string, size: string, fp?: any) => {
    const id = fp?.id || 'unknown'
    const lat = typeof fp?.lat === 'number' ? fp.lat : null
    const lon = typeof fp?.lon === 'number' ? fp.lon : null
    const error = typeof fp?.error === 'number' ? fp.error : null
    const dynamicColor = errorToColor(error ?? 10)
    // In "show all" mode a marker represents a cluster of samples at one spot, so
    // clicking it bulk-deletes the whole coordinate; otherwise delete the single fp.
    const canClusterDelete = showAllFingerprints && lat !== null && lon !== null
    const deleteCall = canClusterDelete
      ? `window.deleteFingerprintCluster(${lat}, ${lon})`
      : `window.deleteFingerprint('${id}')`
    const successMsg = canClusterDelete ? 'Deleted fingerprint cluster' : `Deleted fingerprint: ${id}`
    const heading = canClusterDelete ? 'Fingerprint Cluster' : 'Fingerprint'
    const rssiEntries = Object.entries((fp?.data as Record<string, number>) || {})
      .sort(([, rssiA], [, rssiB]) => rssiB - rssiA)
      .map(([k, v]) => `<div style='font-family: monospace;'>${k}: ${v}</div>`)
      .join('')

    return `
    <div
      class="fingerprint-marker"
      style="display:flex;flex-direction:column;align-items:center;text-align:center;cursor:pointer;pointer-events:auto;opacity:1;z-index:9999;"
      onclick="
        const modal = document.createElement('div');
        modal.setAttribute('role','dialog');
        modal.style.cssText = 'position:fixed;top:30%;left:50%;transform:translateX(-50%);background:white;border:1px solid #ccc;padding:20px;z-index:99999;font-family:sans-serif;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.2);min-width:300px';
        modal.innerHTML = \`
          <div style='margin-bottom:12px;'><strong>${heading}</strong></div>
          <div><strong>ID:</strong> \${'${id}'}</div>
          <div><strong>Error:</strong> ${error?.toFixed?.(2) ?? 'N/A'}</div>
          <div style='margin:10px 0; max-height:150px; overflow-y:auto; border-top:1px solid #eee; padding-top:8px;'>
            ${rssiEntries || '<em>No RSSI data</em>'}
          </div>
          <div style='margin-top:12px; text-align:right;'>
            <button id='delete-confirm' style='margin-right:8px; background:red; color:white; padding:6px 12px; border:none; border-radius:4px; cursor:pointer;'>Delete</button>
            <button onclick='this.parentElement.parentElement.remove()' style='padding:6px 12px; border:1px solid #ccc; border-radius:4px; cursor:pointer;'>Close</button>
          </div>
        \`;
        document.body.appendChild(modal);
        modal.querySelector('#delete-confirm').onclick = () => {
          ${deleteCall}
            .then(() => { alert('✅ ${successMsg}'); document.body.removeChild(modal); })
            .catch(err => { alert('❌ Error: ' + (err?.message || err)); });
        };
      "
    >
      <div style="font-size:${size}; color:${dynamicColor};">
        <i class="fas fa-fingerprint"></i>
      </div>
      <div style="font-size:9px;line-height:1.1;color:#333;background:#fff;border-radius:3px;padding:0.5px 2px;margin-top:0;">
        ${error?.toFixed?.(1) ?? '–'}
      </div>
    </div>
  `
  }

  useEffect(() => {
    lastBeaconsRef.current = lastBeacons
  }, [lastBeacons])

  // --- Camera helpers --------------------------------------------------------

  // Always build a Coordinate and use Camera.set with it.
  const setCameraCenter = useCallback(
    (centerCoord: Coordinate, opts?: Partial<TCameraTarget>) => {
      if (!mapView) return
      const target: TCameraTarget = {
        bearing: 0,
        pitch: 0,
        zoomLevel: 21,
        center: centerCoord,
        ...opts,
      }
      setCameraPosition(target)
      mapView.Camera.set(target, { animate: true, duration: 900 })
    },
    [mapView],
  )

  // Ensure correct floor is set, then center on the given lat/lon.
  const centerCameraOnLatLon = useCallback(
    async (floorObj: any, lat: number, lon: number, zoomLevel = 21) => {
      if (!mapView || !mapData) return
      // 1) set floor (await)
      await mapView.setFloor(floorObj)
      // 2) build Coordinate on that floor
      const coord = new Coordinate({ latitude: lat, longitude: lon, floorId: toFloorId(floorObj.id) })
      // 3) defer one frame so the floor render finishes
      requestAnimationFrame(() => setCameraCenter(coord, { zoomLevel }))
    },
    [mapView, mapData, setCameraCenter],
  )

  const centerOnTriPoint = useCallback(
    async (floorObj: any, lat?: number, lon?: number) => {
      if (!mapView || !mapData || typeof lat !== 'number' || typeof lon !== 'number' || !floorObj) return false
      const key = `tri:${floorObj.id}:${lat.toFixed(6)},${lon.toFixed(6)}`
      if (lastCameraKeyRef.current === key) return true
      await centerCameraOnLatLon(floorObj, lat, lon, 21)
      lastCameraKeyRef.current = key
      return true
    },
    [mapView, mapData, centerCameraOnLatLon],
  )

  const centerOnSpaceIfPossible = useCallback(
    async (space: any) => {
      const lat = space?.centroid?.latitude ?? space?.center?.latitude
      const lon = space?.centroid?.longitude ?? space?.center?.longitude
      const floorObj = space?.floor
      if (typeof lat === 'number' && typeof lon === 'number' && floorObj?.id) {
        const key = `space:${space.id}:${lat.toFixed(6)},${lon.toFixed(6)}`
        if (lastCameraKeyRef.current === key) return true
        await centerCameraOnLatLon(floorObj, lat, lon, 20)
        lastCameraKeyRef.current = key
        return true
      }
      return false
    },
    [centerCameraOnLatLon],
  )

  // Show labels only for spaces on the given floor; clear the rest.
  const showLabelsForFloor = useCallback(
    (floorId: string | number | undefined | null) => {
      if (!mapView || !mapData || floorId == null) return
      mapView.Labels.removeAll()
      mapData.getByType('space').forEach((space: any) => {
        if (space.floor?.id === floorId) mapView.Labels.add(space, space.name)
      })
    },
    [mapView, mapData],
  )

  // --------------------------------------------------------------------------

  useEffect(() => {
    async function init() {
      if (!selectedOrg?.mappedin) return

      await getFloors(alertSession.orgId)

      const { mapId, key, secret } = selectedOrg.mappedin
      const md = await getMapData({ mapId, key, secret })
      const mv = await show3dMap(document.getElementById('mappedin-map') as HTMLDivElement, md)

      const defaultCamera: TCameraTarget = {
        bearing: 0,
        pitch: 0,
        zoomLevel: 20,
        center: mv.Camera.center,
      }

      setMapData(md)
      setMapView(mv)
      mapViewRef.current = mv
      setCameraPosition(defaultCamera)
      mv.Camera.set(defaultCamera)
      setInitialized(true)
    }

    if (!initialized) init()
  }, [selectedOrg, initialized, alertSession?.orgId])

  // Destroy the WebGL 3D map view on unmount to release the GPU context.
  // Without this, opening/closing the drawer leaks a context each time, which
  // eventually exhausts GPU memory and hangs the renderer (RESULT_CODE_HUNG).
  useEffect(() => {
    return () => {
      try {
        mapViewRef.current?.destroy?.()
      } catch (err) {
        console.error('Error destroying mappedin map view:', err)
      }
      mapViewRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!mapView || !fingerprintMode) return
    window.dispatchEvent(new Event('resize'))
  }, [mapView, fingerprintMode])

  // Switch mappedin floor when selectedFloor changes (no centering here)
  useEffect(() => {
    if (!mapData || !mapView || !selectedFloor?.mappedinId) return
    const mappedinFloors = mapData.getByType('floor') as any[]
    const floor = mappedinFloors.find((f: any) => f.id === selectedFloor.mappedinId)
    if (floor) {
      mapView.setFloor(floor)
      showLabelsForFloor(floor.id)
    }
  }, [selectedFloor, mapData, mapView, showLabelsForFloor])

  const updateMapWithAlertSession = useCallback(async () => {
    if (!mapView || !mapData || !alertSession?.lastLocation) return

    const loc = alertSession.lastLocation
    mapView.Markers.removeAll()

    // reset spaces
    mapData.getByType('space').forEach((space: any) => {
      mapView.updateState(space, { color: '#f5f5f5', hoverColor: '#D3D3D3', interactive: true })
    })

    // highlight matches
    if (loc.spaceName) {
      mapData.getByType('space').forEach((space: any) => {
        if (space.name === loc.spaceName) mapView.updateState(space, { color: '#a4abae', interactive: true })
      })
    }
    if (loc.room && loc.room !== loc.spaceName) {
      mapData.getByType('space').forEach((space: any) => {
        if (space.name === loc.room) mapView.updateState(space, { color: '#a4abae', interactive: true })
      })
    }

    const mappedinFloors = mapData.getByType('floor') as any[]
    let floor = mappedinFloors.find((f: any) => f.id === loc.floorMappedinId)
    if (!floor) {
      floor = mapData.getByType('floor').find((f: any) => f.name === loc.floor)
      if (!floor) setCanUseMappedin(false)
      if (!floor) console.log('!! could not find floor for', loc.floorMappedinId, loc.floor)
    } else {
      setCanUseMappedin(true)
    }

    if (floor) {
      // ensure floor switch completes before adding/centering
      await mapView.setFloor(floor)
      showLabelsForFloor(floor.id)
    }

    // Place the tri dot and CENTER on it (authoritative)
    let centered = false
    if (floor && typeof loc.triLat === 'number' && typeof loc.triLong === 'number') {
      const triCoord = new Coordinate({ latitude: loc.triLat, longitude: loc.triLong, floorId: toFloorId(floor.id) })
      mapView.Markers.add(triCoord, dot(BRAND_COLOR, '12px'), {
        interactive: true,
        anchor: 'center',
        rank: 'always-visible',
      })
      await centerOnTriPoint(floor, loc.triLat, loc.triLong)
      centered = true
    }

    // If we have no tri point, fall back to centering on the room/space
    if (!centered) {
      const spaces = mapData.getByType('space') as any[]
      const byName =
        (loc.room && spaces.find(s => s.name === loc.room)) ||
        (loc.spaceName && spaces.find(s => s.name === loc.spaceName)) ||
        null
      if (byName) await centerOnSpaceIfPossible(byName)
    }
  }, [
    mapView,
    mapData,
    alertSession?.lastLocation,
    setCanUseMappedin,
    centerOnTriPoint,
    centerOnSpaceIfPossible,
    showLabelsForFloor,
  ])

  // Update when alertSession changes (non-fingerprint mode)
  useEffect(() => {
    if (!fingerprintMode) updateMapWithAlertSession()
    setLastLocation(alertSession?.lastLocation)
  }, [fingerprintMode, updateMapWithAlertSession, alertSession?.lastLocation])

  // Fetch every stored fingerprint for the selected floor while "show all" is on.
  // Re-runs after a cluster delete (refreshTick) so removed markers disappear.
  useEffect(() => {
    if (!fingerprintMode || !showAllFingerprints || !selectedFloor?.id || !alertSession?.orgId) {
      setAllFingerprints([])
      return
    }

    let cancelled = false
    getAllFingerprintsForFloor({ orgId: alertSession.orgId, floorId: selectedFloor.id })
      .then(fps => {
        if (!cancelled) setAllFingerprints(fps)
      })
      .catch(err => {
        console.error('❌ Error loading all fingerprints for floor:', err)
        if (!cancelled) setAllFingerprints([])
      })

    return () => {
      cancelled = true
    }
  }, [fingerprintMode, showAllFingerprints, selectedFloor?.id, alertSession?.orgId, refreshTick])

  // Load fingerprints for selected floor in fingerprint mode
  useEffect(() => {
    if (!mapView || !mapData || !selectedFloor?.id || !selectedFloor.mappedinId || !fingerprintMode) return

    async function loadFingerprintsForFloor() {
      try {
        setFingerprintMarkers([])
        mapView.Markers.removeAll()

        const mappedinFloors = mapData.getByType('floor') as any[]
        const floor = mappedinFloors.find((f: any) => f.id === selectedFloor.mappedinId)
        if (!floor) return

        await mapView.setFloor(floor)

        const reps = dedupeFingerprintsBySpot(fingerprintData || [], selectedFloor.id, 5)

        reps.forEach(fp => {
          if (typeof fp.lat !== 'number' || typeof fp.lon !== 'number') return
          const coord = new Coordinate({
            latitude: fp.lat,
            longitude: fp.lon,
            floorId: toFloorId(floor.id),
          })
          const marker = mapView.Markers.add(coord, fpDot('#222', '14px', fp), {
            interactive: true,
            anchor: 'center',
            rank: 'always-visible',
          })
          setFingerprintMarkers(prev => [...prev, { coordinate: coord, mappedinFloorId: floor.id, marker }])
        })

        // (Optional) center on best fingerprint:
        // const best = pickFingerprintWithLowestError(reps)
        // if (best && typeof best.lat === 'number' && typeof best.lon === 'number') {
        //   const key = `fp:${floor.id}:${best.lat.toFixed(6)},${best.lon.toFixed(6)}`
        //   if (lastCameraKeyRef.current !== key) {
        //     await centerCameraOnLatLon(floor, best.lat, best.lon, 21)
        //     lastCameraKeyRef.current = key
        //   }
        // }

        console.log(
          `📌 Plotted ${reps.length} fingerprint markers (lowest-error per spot, from ${
            (fingerprintData || []).length
          } samples) on ${selectedFloor.name}`,
        )
      } catch (err) {
        console.error('❌ Error loading fingerprints:', err)
      }
    }

    window.dispatchEvent(new Event('resize'))
    loadFingerprintsForFloor()
  }, [mapView, mapData, selectedFloor, fingerprintMode, showAllFingerprints, fingerprintData, centerCameraOnLatLon])

  return <div style={{ width: '800px', height: '800px' }} id="mappedin-map" />
}

export default VenueMap3
